import React, { useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { makeStyles, Typography } from '@material-ui/core'
import EditableDialogWrapper from './editableDialogWrapper'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { dateTimeConstants } from '../../constants/datetime.constants'
import { parseToUTCDate } from '../../helpers/dateFns'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

const useStyles = makeStyles(() => ({
    mediumWidth: {
        width: '50%',
    },
    // Override Material-UI's default error color (#F44336, 3.68:1 contrast)
    // with #AA1308 (5.92:1 contrast) to meet WCAG 2.2 AA 1.4.3 (4.5:1 required)
    errorText: {
        '&.Mui-error': {
            color: '#AA1308',
        },
    },
}))

interface DatePickerDialogProps {
    title: string
    description?: string
    open: boolean
    onConfirm: (date: Date | null) => void
    onReset: (date: Date) => void
    onClose: () => void
    currentDate: string
    onChange: (date: Date | null) => void
    resetButtonVisible?: boolean
    confirmButtonText?: string
    dateFormat?: string
}

const DatePickerDialog = ({
    title,
    description,
    open,
    onConfirm,
    onReset,
    onClose,
    currentDate,
    onChange,
    resetButtonVisible = true,
    confirmButtonText,
    dateFormat = dateTimeConstants.DEFAULT_DATE_FORMAT,
}: DatePickerDialogProps) => {
    const classes = useStyles()
    const [date, setDate] = useState<Date>(
        parseToUTCDate(currentDate, dateFormat) || new Date()
    )

    const renderDatePicker = () => (
        <KeyboardDatePicker
            className={classes.mediumWidth}
            format={dateFormat}
            value={date}
            onChange={(selectedDate: MaterialUiPickersDate) => {
                setDate(selectedDate ?? new Date())
                onChange(selectedDate)
            }}
            KeyboardButtonProps={{ 'aria-label': 'Open date picker' }}
            inputProps={{ 'aria-label': title }}
            FormHelperTextProps={{
                className: classes.errorText,
            }}
        />
    )

    return (
        <EditableDialogWrapper title={title} open={open} onClose={onClose}>
            <DialogContent dividers>
                {description && (
                    <Typography gutterBottom>{description}</Typography>
                )}
                {renderDatePicker()}
            </DialogContent>
            <DialogActions>
                {resetButtonVisible && (
                    <Button
                        onClick={() => {
                            const now = new Date()
                            setDate(now)
                            onReset(now)
                        }}
                        color='secondary'
                    >
                        Reset
                    </Button>
                )}
                <Button
                    variant='contained'
                    onClick={() => onConfirm(date)}
                    color='secondary'
                >
                    {confirmButtonText || 'Confirm'}
                </Button>
            </DialogActions>
        </EditableDialogWrapper>
    )
}

export default DatePickerDialog
